import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {
  ButtonConfig,
  buttonsStaticConfig,
  FireEvent,
  GeneralSandbox,
  Icon,
  inputErrorTypes,
  NavigationService,
  NotificationEnum,
  NotificationService, NotificationTimes, RoutesEnum
} from '../../../modules/global';
import { PageContainerComponent } from '../../../modules/shared/components/page-container/page-container.component';
import { TitledCardComponent } from '../../../modules/shared/components/titled-card/titled-card.component';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { TextInputComponent } from '../../../modules/shared/components/inputs/text-input/text-input.component';
import { TextAreaComponent } from '../../../modules/shared/components/inputs/text-area/text-area.component';
import { SimpleDragAndDropComponent } from '../../../modules/shared/components/simple-drag-and-drop/simple-drag-and-drop.component';
import { NgIf, NgFor } from '@angular/common';
import { ButtonIconComponent } from '../../../modules/shared/components/buttons/button-icon/button-icon.component';
import { BottomBarComponent } from '../../../modules/shared/components/bottom-bar/bottom-bar.component';

@Component({
    selector: 'slm-report',
    templateUrl: './bug-report.container.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [PageContainerComponent, TitledCardComponent, TranslateDirective, TextInputComponent, TextAreaComponent, SimpleDragAndDropComponent, NgIf, NgFor, ButtonIconComponent, BottomBarComponent, TranslatePipe]
})
export class BugReportContainer implements OnInit {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly generalSandbox = inject(GeneralSandbox);
  private readonly navigation = inject(NavigationService);
  private readonly notification = inject(NotificationService);

  /**
   * translation parameters
   */
  public readonly links  = {
   faqUrl: `/${RoutesEnum.FAQ}`,
   videoUrl: `/${RoutesEnum.VIDEOS}`
  };

  /**
   * Icon enumeration
   */
  public readonly icons = Icon;

  /**
   * @type {Array<InputError>}
   * List of input error texts
   */
  public readonly errors = [
    inputErrorTypes.required,
    inputErrorTypes.minLength
  ];

  /**
   * @type {Array<ButtonConfig>}
   * Button list configuration, but some modification. These buttons are aligned to right in the </slm-bottom-bar>.
   */
  public readonly buttons: Array<ButtonConfig> = [
    {
      ...buttonsStaticConfig.cancel,
      testId: 'cancelButton',
      align: 'right'
    },
    {
      ...buttonsStaticConfig.send,
      label: 'BUG_REPORT.SEND',
      testId: 'sendButton',
      align: 'right'
    }
  ];

  public form: UntypedFormGroup;

  /**
   * @type {Array<File>}
   * Users can attach some images (png/jpg/jpeg) to the bug report. This list will contain the file (image) list.
   */
  public readonly files: Array<File> = [];

  ngOnInit() {
    /**
     * Initialization of the reactive form with the subject of the report and a description of the report
     */
    this.form = this.fb.group({
      subject: ['', [Validators.required]],
      description: ['', [Validators.required, Validators.minLength(30)]]
    });

    document.addEventListener('paste', (_event) => {});
  }

  /**
   *
   * @param files {File} - add selected or drag and droped files
   */
  public addFile(files: Array<File>) {
    this.files.push(...files);
    this.cd.detectChanges();
  }

  /**
   *
   * @param index {number} - remove selected file from the files array
   */
  public removeFile(index: number) {
    if (index >= 0 && index < this.files.length) {
      this.files.splice(index, 1);
      this.cd.detectChanges();
    }
  }

  public buttonClicked(event: FireEvent) {
    if (event.event === 'send') {
      this.saveReport();
    } else {
      this.navigation.goBack();
    }
  }

  /**
   *
   * Submit values
   */
  public saveReport() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.cd.detectChanges();
      return;
    }

    this.buttons[1].isLoading = true;
    this.cd.detectChanges();
    const {subject, description} = this.form.value;
    this.generalSandbox.sendReport(subject, description, this.files).subscribe({
      next: () => {
        this.notification.notify('BUG_REPORT.REPORTED', NotificationEnum.SUCCESS, NotificationTimes.INFINITE);
        this.files.length = 0;
        this.buttons[1].isLoading = false;
        this.form.reset();
      },
      error: () => {
        this.buttons[1].isLoading = false;
        this.cd.detectChanges();
      }
    });
  }

  public onPaste(event: ClipboardEvent): void {
    const fileList = event.clipboardData.files;
    if(fileList){
      const files = [];
      for (let index = 0; index < (fileList || []).length; ++index) {
        files.push(fileList.item(index));
      }

      this.addFile(files);
    }
  }

  public trackByIndex = (index, _) => index;
}
